/**
 * Environment utility functions for determining current application environment
 */

export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentInfo {
  name: Environment;
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
}

/**
 * Get the current environment from VITE_APP_ENV or infer from MODE
 */
export function getCurrentEnvironment(): Environment {
  const viteAppEnv = import.meta.env.VITE_APP_ENV;

  // If explicitly set, use that
  if (viteAppEnv) {
    return viteAppEnv as Environment;
  }

  // Otherwise infer from Vite MODE
  const mode = import.meta.env.MODE;
  if (mode === 'production') {
    return 'production';
  }

  // Default to development for any other mode
  return 'development';
}

/**
 * Get comprehensive environment information
 */
export function getEnvironmentInfo(): EnvironmentInfo {
  const name = getCurrentEnvironment();

  return {
    name,
    isDevelopment: name === 'development',
    isStaging: name === 'staging',
    isProduction: name === 'production'
  };
}

/**
 * Check if current environment is production
 */
export function isProduction(): boolean {
  return getCurrentEnvironment() === 'production';
}

/**
 * Check if current environment is staging
 */
export function isStaging(): boolean {
  return getCurrentEnvironment() === 'staging';
}

/**
 * Check if current environment is development
 */
export function isDevelopment(): boolean {
  return getCurrentEnvironment() === 'development';
}

/**
 * Get build metadata from environment variables
 */
export interface BuildMetadata {
  environment: Environment;
  buildNumber?: string;
  commitSha?: string;
  buildTime?: string;
  version?: string;
}

export function getBuildMetadata(): BuildMetadata {
  return {
    environment: getCurrentEnvironment(),
    buildNumber: import.meta.env.VITE_BUILD_NUMBER,
    commitSha: import.meta.env.VITE_COMMIT_SHA?.substring(0, 7),
    buildTime: import.meta.env.VITE_BUILD_TIME,
    version: import.meta.env.VITE_APP_VERSION || '1.0.0'
  };
}
